import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpenIcon,
  AcademicCapIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { H1, H2, Body, Card, Badge, Spinner } from '../components/ui';
import { useLearningAreas, useLearningOutcomes } from '../hooks/api/useAssessments';
import type { LearningArea, LearningOutcome } from '../types/api';

const Curriculum: React.FC = () => {
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const { data: areas = [], isLoading: areasLoading, error: areasError } = useLearningAreas();
  const { data: outcomes = [], isLoading: outcomesLoading } = useLearningOutcomes();

  const isLoading = areasLoading || outcomesLoading;

  // Group outcomes by learning area
  const outcomesByArea = outcomes.reduce((acc, outcome) => {
    const areaId = outcome.learning_area_id;
    if (!acc[areaId]) acc[areaId] = [];
    acc[areaId].push(outcome);
    return acc;
  }, {} as Record<string, LearningOutcome[]>);

  // Filter by search
  const filteredAreas = areas.filter((area) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const areaMatches =
      area.name.toLowerCase().includes(query) ||
      area.code.toLowerCase().includes(query) ||
      area.description?.toLowerCase().includes(query);
    const outcomeMatches = (outcomesByArea[area.id] || []).some(
      (o) =>
        o.code.toLowerCase().includes(query) ||
        o.description.toLowerCase().includes(query)
    );
    return areaMatches || outcomeMatches;
  });

  const toggleArea = (areaId: string) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  };

  const expandAll = () => setExpandedAreas(new Set(areas.map((a) => a.id)));
  const collapseAll = () => setExpandedAreas(new Set());

  // Area color palette
  const areaColors: Record<number, { bg: string; text: string; icon: string; badge: string }> = {
    0: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', icon: 'text-blue-500', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    1: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', icon: 'text-purple-500', badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
    2: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', icon: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
    3: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    4: { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-300', icon: 'text-rose-500', badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
    5: { bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', icon: 'text-cyan-500', badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300' },
  };
  const getColor = (index: number) => areaColors[index % Object.keys(areaColors).length];

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (areasError) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <BookOpenIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <H2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Unable to load curriculum
          </H2>
          <Body className="text-gray-600 dark:text-gray-400">
            Please check your connection and try again.
          </Body>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <H1 className="mb-2">Curriculum</H1>
        <Body className="text-gray-600 dark:text-gray-400">
          Learning areas and outcomes for your school's curriculum.
        </Body>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-primary-600">{areas.length}</div>
          <Body className="text-sm text-gray-500 dark:text-gray-400">Learning Areas</Body>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{outcomes.length}</div>
          <Body className="text-sm text-gray-500 dark:text-gray-400">Learning Outcomes</Body>
        </Card>
        <Card className="p-4 text-center hidden md:block">
          <div className="text-2xl font-bold text-blue-600">
            {outcomes.length > 0 && areas.length > 0
              ? Math.round(outcomes.length / areas.length)
              : 0}
          </div>
          <Body className="text-sm text-gray-500 dark:text-gray-400">Avg Outcomes per Area</Body>
        </Card>
      </div>

      {/* Search & Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search areas or outcomes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
          >
            Expand All
          </button>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <button
            onClick={collapseAll}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Learning Areas */}
      <div className="space-y-4">
        {filteredAreas.map((area, index) => {
          const areaOutcomes = outcomesByArea[area.id] || [];
          const isExpanded = expandedAreas.has(area.id);
          const color = getColor(index);

          return (
            <motion.div
              key={area.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className="overflow-hidden">
                {/* Area Header */}
                <button
                  onClick={() => toggleArea(area.id)}
                  className="w-full p-5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2.5 rounded-lg ${color.bg}`}>
                      <BookOpenIcon className={`w-6 h-6 ${color.icon}`} />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-3">
                        <H2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {area.name}
                        </H2>
                        <span className={`text-xs font-mono px-2 py-0.5 rounded ${color.badge}`}>
                          {area.code}
                        </span>
                      </div>
                      {area.description && (
                        <Body className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          {area.description}
                        </Body>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" size="sm">
                      {areaOutcomes.length} outcome{areaOutcomes.length !== 1 ? 's' : ''}
                    </Badge>
                    {isExpanded ? (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Outcomes List */}
                {isExpanded && areaOutcomes.length > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-gray-100 dark:border-gray-700"
                  >
                    <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                      {areaOutcomes.map((outcome) => (
                        <div
                          key={outcome.id}
                          className="px-5 py-3 flex items-start gap-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
                        >
                          <CheckCircleIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${color.icon}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                {outcome.code}
                              </span>
                            </div>
                            <Body className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                              {outcome.description}
                            </Body>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Empty outcomes */}
                {isExpanded && areaOutcomes.length === 0 && (
                  <div className="border-t border-gray-100 dark:border-gray-700 px-5 py-6 text-center">
                    <Body className="text-sm text-gray-400">
                      No learning outcomes defined for this area.
                    </Body>
                  </div>
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredAreas.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <BookOpenIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <H2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery ? 'No matching results' : 'No curriculum found'}
          </H2>
          <Body className="text-gray-600 dark:text-gray-400">
            {searchQuery
              ? 'Try adjusting your search terms.'
              : 'No learning areas have been set up yet.'}
          </Body>
        </div>
      )}
    </div>
  );
};

export default Curriculum;
